import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getFontById } from '../../constants/fonts';

/**
 * LivePreviewCanvas Component
 *
 * Full-canvas implementation with native drag-and-drop for caption positioning
 * Eliminates React re-render issues by rendering everything directly on canvas
 *
 * Architecture:
 * - Canvas Layer 1: Background image (aspect-ratio scaled)
 * - Canvas Layer 2: Text with outline, shadows, and optional background
 * - Canvas Layer 3: Drag indicators (coordinates, manual badge)
 *
 * Mouse Events:
 * - mousedown: Hit detection on text bounds
 * - mousemove: Real-time position update + redraw
 * - mouseup: Finalize and notify parent
 *
 * Coordinates:
 * - Canvas: 1080x1920 (Instagram Story dimensions)
 * - Display: Responsive container (scaled)
 * - Position: {x: center, y: top} matching backend ImageRenderer
 */
export const LivePreviewCanvas = ({
  imageUrl,
  captionText,
  style,
  textPosition,
  originalPosition,
  onPositionChange = () => {},
  textAreaWidth = 800,
  fontSize = 48,
  textAlignment = 'left',
  isPositionOverridden = false,
  onResetPosition,
  gifMetadata,
  onGifChange = () => {},
  canvasBackgroundColor = null,
  backgroundMode = 'solid',
  backgroundConfig = {},
  hasWrongAspectRatio = false,
  backgroundMetadata = null,
  fontName = 'Classic'
}) => {
  // Get font family from font name
  const fontFamily = getFontById(fontName)?.family || 'Lato';
  const fontString = `bold ${fontSize}px "${fontFamily}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif`;

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imageRef = useRef(new Image());
  const imageLoadedRef = useRef(false);  // Track if image is loaded to avoid re-loading
  const redrawRef = useRef(null);  // Stable reference to redraw function

  // Drag state (local only, parent notified on mouseup)
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartOffset, setDragStartOffset] = useState({ x: 0, y: 0 });
  const [currentPosition, setCurrentPosition] = useState(
    textPosition || { x: 540, y: 1700 }
  );
  const [containerDimensions, setContainerDimensions] = useState({ width: 360, height: 640 });
  const [isHovering, setIsHovering] = useState(false);

  // GIF state
  const [gifImage, setGifImage] = useState(null);
  const [isDraggingGif, setIsDraggingGif] = useState(false);
  const [gifDragStartOffset, setGifDragStartOffset] = useState({ x: 0, y: 0 });

  // Canvas dimensions (Instagram Story)
  const CANVAS_WIDTH = 1080;
  const CANVAS_HEIGHT = 1920;

  // Text rendering constants
  const LINE_HEIGHT = fontSize + 20; // 68px for fontSize=48
  const OUTLINE_WIDTH = 2;
  const BG_HORIZONTAL_MARGIN = 30; // horizontal_margin in backend (30px left/right)
  const BG_VERTICAL_PADDING_TOP = 8; // vertical_padding on first line top
  const BG_VERTICAL_PADDING_BOTTOM = 40; // Extra padding on last line bottom (magic number!)
  const BG_BORDER_RADIUS = 15;

  // Calculate scale factors
  const scaleX = containerDimensions.width / CANVAS_WIDTH;
  const scaleY = containerDimensions.height / CANVAS_HEIGHT;

  /**
   * Wrap text to fit within max width
   */
  const wrapText = useCallback((text, maxWidth, ctx) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = [];

    for (let i = 0; i < words.length; i++) {
      const testLine = [...currentLine, words[i]].join(' ');
      const metrics = ctx.measureText(testLine);

      if (metrics.width <= maxWidth) {
        currentLine.push(words[i]);
      } else {
        if (currentLine.length > 0) {
          lines.push(currentLine.join(' '));
          currentLine = [words[i]];
        } else {
          lines.push(words[i]);
        }
      }
    }

    if (currentLine.length > 0) {
      lines.push(currentLine.join(' '));
    }

    return lines;
  }, []);

  /**
   * Get maximum line width (for centering and hit detection)
   */
  const getMaxLineWidth = useCallback((lines, ctx) => {
    let maxWidth = 0;
    lines.forEach(line => {
      const metrics = ctx.measureText(line);
      maxWidth = Math.max(maxWidth, metrics.width);
    });
    return maxWidth;
  }, []);

  /**
   * Draw text with outline (entire string at once)
   */
  const drawTextWithOutline = useCallback((ctx, text, x, y, fillColor, outlineColor, outlineWidth) => {
    // Draw outline
    for (let adjX = -outlineWidth; adjX <= outlineWidth; adjX++) {
      for (let adjY = -outlineWidth; adjY <= outlineWidth; adjY++) {
        ctx.fillStyle = outlineColor;
        ctx.fillText(text, x + adjX, y + adjY);
      }
    }

    // Draw text
    ctx.fillStyle = fillColor;
    ctx.fillText(text, x, y);
  }, []);

  /**
   * Draw rounded rectangle (for text backgrounds)
   */
  const drawRoundedRect = useCallback((ctx, x1, y1, x2, y2, radius, fillColor) => {
    const width = x2 - x1;
    const height = y2 - y1;

    ctx.fillStyle = fillColor;
    ctx.beginPath();
    ctx.moveTo(x1 + radius, y1);
    ctx.lineTo(x2 - radius, y1);
    ctx.quadraticCurveTo(x2, y1, x2, y1 + radius);
    ctx.lineTo(x2, y2 - radius);
    ctx.quadraticCurveTo(x2, y2, x2 - radius, y2);
    ctx.lineTo(x1 + radius, y2);
    ctx.quadraticCurveTo(x1, y2, x1, y2 - radius);
    ctx.lineTo(x1, y1 + radius);
    ctx.quadraticCurveTo(x1, y1, x1 + radius, y1);
    ctx.closePath();
    ctx.fill();
  }, []);

  /**
   * Calculate text bounding box for hit detection
   */
  const calculateTextBounds = useCallback((position, lines, ctx) => {
    if (!lines || lines.length === 0) {
      return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
    }

    const maxWidth = getMaxLineWidth(lines, ctx);
    const totalHeight = lines.length * LINE_HEIGHT;

    // Position.x is the CENTER of the text block
    const left = position.x - maxWidth / 2;
    const right = position.x + maxWidth / 2;
    const top = position.y;
    const bottom = position.y + totalHeight;

    return { left, top, right, bottom, width: maxWidth, height: totalHeight };
  }, [LINE_HEIGHT, getMaxLineWidth]);

  /**
   * Check if point is within text bounds
   */
  const isPointInText = useCallback((canvasX, canvasY, bounds) => {
    return canvasX >= bounds.left &&
           canvasX <= bounds.right &&
           canvasY >= bounds.top &&
           canvasY <= bounds.bottom;
  }, []);

  /**
   * Complete redraw: background + text + indicators
   *
   * PERFORMANCE NOTE: Uses imageLoadedRef to avoid re-loading image on every drag update
   * Image is only loaded once when imageUrl changes, preventing flickering
   */
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Clear canvas
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // === LAYER 0.5: Canvas Background (for non-9:16 images) ===
    if (hasWrongAspectRatio) {
      if (backgroundMode === 'gradient' && backgroundConfig.gradientColors && backgroundConfig.gradientColors.length >= 2) {
        // Gradient background
        let gradient;

        if (backgroundConfig.gradientType === 'linear') {
          // Linear gradient with angle
          const angleRad = (backgroundConfig.gradientAngle || 45) * Math.PI / 180;

          // Calculate gradient line endpoints based on angle
          const centerX = CANVAS_WIDTH / 2;
          const centerY = CANVAS_HEIGHT / 2;
          const maxDistance = Math.sqrt(CANVAS_WIDTH * CANVAS_WIDTH + CANVAS_HEIGHT * CANVAS_HEIGHT) / 2;

          const x0 = centerX - Math.cos(angleRad) * maxDistance;
          const y0 = centerY - Math.sin(angleRad) * maxDistance;
          const x1 = centerX + Math.cos(angleRad) * maxDistance;
          const y1 = centerY + Math.sin(angleRad) * maxDistance;

          gradient = ctx.createLinearGradient(x0, y0, x1, y1);
        } else {
          // Radial gradient from center
          gradient = ctx.createRadialGradient(
            CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, 0,
            CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, Math.max(CANVAS_WIDTH, CANVAS_HEIGHT) / 2
          );
        }

        // Add color stops
        const colors = backgroundConfig.gradientColors;
        colors.forEach((color, index) => {
          const position = index / (colors.length - 1);
          gradient.addColorStop(position, color);
        });

        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      } else if (canvasBackgroundColor) {
        // Solid color background
        ctx.fillStyle = canvasBackgroundColor;
        ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      }
    }

    // === LAYER 1: Background Image ===
    // Use cached image reference - avoids flickering during drag
    if (imageLoadedRef.current && imageRef.current && imageRef.current.complete) {
      const imageAspect = imageRef.current.naturalWidth / imageRef.current.naturalHeight;
      const canvasAspect = CANVAS_WIDTH / CANVAS_HEIGHT;

      let drawWidth, drawHeight, offsetX = 0, offsetY = 0;

      if (hasWrongAspectRatio) {
        // CONTAIN MODE: Scale image to fit entirely within canvas (with colored background)
        if (imageAspect > canvasAspect) {
          // Image is wider - fit by width
          drawWidth = CANVAS_WIDTH;
          drawHeight = CANVAS_WIDTH / imageAspect;
          offsetX = 0;
          offsetY = (CANVAS_HEIGHT - drawHeight) / 2;
        } else {
          // Image is taller - fit by height
          drawHeight = CANVAS_HEIGHT;
          drawWidth = CANVAS_HEIGHT * imageAspect;
          offsetX = (CANVAS_WIDTH - drawWidth) / 2;
          offsetY = 0;
        }
      } else {
        // COVER MODE: Scale image to cover entire canvas (crop if needed)
        if (imageAspect > canvasAspect) {
          // Image is wider - scale by height
          drawHeight = CANVAS_HEIGHT;
          drawWidth = CANVAS_HEIGHT * imageAspect;
          offsetX = (CANVAS_WIDTH - drawWidth) / 2;
        } else {
          // Image is taller - scale by width
          drawWidth = CANVAS_WIDTH;
          drawHeight = CANVAS_WIDTH / imageAspect;
          offsetY = (CANVAS_HEIGHT - drawHeight) / 2;
        }
      }

      ctx.drawImage(imageRef.current, offsetX, offsetY, drawWidth, drawHeight);
    } else {
      // Placeholder
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      ctx.fillStyle = '#9ca3af';
      ctx.font = '32px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Bild wird geladen...', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
    }

    // === LAYER 2: Text Rendering ===
    if (captionText && captionText.trim()) {
      // Set font
      ctx.font = fontString;
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';  // Always left - we calculate positions manually

      // Wrap text
      const lines = wrapText(captionText, textAreaWidth, ctx);
      const maxLineWidth = getMaxLineWidth(lines, ctx);

      // Calculate positions (matching backend _get_text_position logic)
      // Backend line 252-253: X is the CENTER of the text block
      const xCenter = Math.max(0, Math.min(CANVAS_WIDTH - 1, currentPosition.x));
      const yBase = Math.max(0, Math.min(CANVAS_HEIGHT - 1, currentPosition.y));

      // Backend line 267: text_block_left = x_center - (max_line_width // 2)
      let textBlockLeft = xCenter - Math.floor(maxLineWidth / 2);

      // Backend lines 270-276: Ensure text block stays within bounds
      if (textBlockLeft < 0) {
        textBlockLeft = 0;
      } else if (textBlockLeft + maxLineWidth > CANVAS_WIDTH) {
        textBlockLeft = CANVAS_WIDTH - maxLineWidth;
      }
      textBlockLeft = Math.max(0, textBlockLeft);

      let y = yBase;

      // Draw background boxes (if enabled) - EXACT BACKEND REPLICATION
      if (style.textHasBackground && style.backgroundColor) {
        const totalLines = lines.length;
        const isFirstLine = (idx) => idx === 0;
        const isLastLine = (idx) => idx === totalLines - 1;

        let currentY = yBase; // Track Y position for each line

        lines.forEach((line, idx) => {
          const metrics = ctx.measureText(line);
          const lineWidth = metrics.width;

          // Get text height from bounding box (for last line calculation)
          const textHeight = Math.abs(metrics.actualBoundingBoxAscent) + Math.abs(metrics.actualBoundingBoxDescent);

          // Backend lines 303-311: Position each line relative to the text block
          let lineX;
          if (textAlignment === 'center') {
            // Backend line 305: Center each line within the max_line_width
            lineX = textBlockLeft + Math.floor((maxLineWidth - lineWidth) / 2);
          } else if (textAlignment === 'right') {
            // Backend line 308: Right-align within the max_line_width
            lineX = textBlockLeft + maxLineWidth - lineWidth;
          } else {  // left
            // Backend line 311: Left-align to text_block_left
            lineX = textBlockLeft;
          }

          // Backend lines 314-319: Final safety clamp
          if (lineWidth >= CANVAS_WIDTH) {
            lineX = 0;  // Force to left edge
          } else {
            lineX = Math.max(0, Math.min(CANVAS_WIDTH - lineWidth - 1, lineX));
          }

          // === HORIZONTAL COORDINATES ===
          // Step 1: Calculate box bounds with 30px margin
          const bgX1Raw = lineX - BG_HORIZONTAL_MARGIN;
          const bgX2Raw = lineX + lineWidth + BG_HORIZONTAL_MARGIN;

          // Step 2: Clamp to canvas bounds (backend lines 374-375)
          const bgX1 = Math.max(0, bgX1Raw);
          const bgX2 = Math.min(CANVAS_WIDTH, bgX2Raw);

          // === VERTICAL COORDINATES - EXACT BACKEND LOGIC ===
          let bgY1, bgY2;

          if (isFirstLine(idx)) {
            // First line: Add 8px padding at top (backend lines 379-380)
            bgY1 = currentY - BG_VERTICAL_PADDING_TOP;

            if (isLastLine(idx)) {
              // Single line caption: first AND last line
              bgY2 = currentY + textHeight + BG_VERTICAL_PADDING_BOTTOM; // 40px extra
            } else {
              // First of multiple lines
              bgY2 = currentY + LINE_HEIGHT; // Extend to next line
            }
          } else if (isLastLine(idx)) {
            // Last line (not first): No top padding, 40px bottom padding
            bgY1 = currentY; // No top padding (backend line 382)
            bgY2 = currentY + textHeight + BG_VERTICAL_PADDING_BOTTOM; // Extra 40px (backend line 386)
          } else {
            // Middle lines: No padding, extend full line_height for bleed effect
            bgY1 = currentY; // No top padding (backend line 382)
            bgY2 = currentY + LINE_HEIGHT; // Extend to next line (backend line 389)
          }

          // Draw rounded rectangle with 15px radius
          drawRoundedRect(ctx, bgX1, bgY1, bgX2, bgY2, BG_BORDER_RADIUS, style.backgroundColor);

          // Advance Y position for next line (backend line 322: y += line_height)
          currentY += LINE_HEIGHT;
        });
      }

      // Reset y for text drawing
      y = yBase;

      // Draw text with outline
      lines.forEach((line, idx) => {
        const metrics = ctx.measureText(line);
        const lineWidth = metrics.width;

        // Backend lines 303-311: Position each line relative to the text block
        let lineX;
        if (textAlignment === 'center') {
          // Backend line 305: Center each line within the max_line_width
          lineX = textBlockLeft + Math.floor((maxLineWidth - lineWidth) / 2);
        } else if (textAlignment === 'right') {
          // Backend line 308: Right-align within the max_line_width
          lineX = textBlockLeft + maxLineWidth - lineWidth;
        } else {  // left
          // Backend line 311: Left-align to text_block_left
          lineX = textBlockLeft;
        }

        // Backend lines 314-319: Final safety clamp
        if (lineWidth >= CANVAS_WIDTH) {
          lineX = 0;  // Force to left edge
        } else {
          lineX = Math.max(0, Math.min(CANVAS_WIDTH - lineWidth - 1, lineX));
        }

        // Draw with outline
        drawTextWithOutline(
          ctx,
          line,
          lineX,
          y,
          style.textColor,
          '#000000',
          OUTLINE_WIDTH
        );

        y += LINE_HEIGHT;
      });
    }

    // === LAYER 3: GIF Rendering ===
    // GIF is now rendered as a separate <img> element outside the canvas
    // for better performance (browser native animation instead of canvas redraws)

    // === LAYER 4: Visual Indicators ===
    if (captionText && captionText.trim()) {
      // Set font
      ctx.font = `bold ${fontSize}px "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif`;
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';

      // Wrap text
      const lines = wrapText(captionText, textAreaWidth, ctx);
      const bounds = calculateTextBounds(currentPosition, lines, ctx);

      // Recalculate xCenter and yBase for indicators
      const xCenter = Math.max(0, Math.min(CANVAS_WIDTH - 1, currentPosition.x));
      const yBase = Math.max(0, Math.min(CANVAS_HEIGHT - 1, currentPosition.y));

      // Hovering/Dragging indicator (dashed border)
      if ((isHovering || isDragging) && bounds.width > 0) {
        ctx.strokeStyle = isDragging ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.4)';
        ctx.lineWidth = 2;
        ctx.setLineDash([8, 4]);
        ctx.strokeRect(bounds.left - 4, bounds.top - 4, bounds.width + 8, bounds.height + 8);
        ctx.setLineDash([]);
      }

      // "Manuell" badge
      if (isPositionOverridden && !isDragging) {
        const badgeX = xCenter - 20;
        const badgeY = yBase - 30;

        ctx.fillStyle = '#3B82F6';
        ctx.fillRect(badgeX, badgeY, 60, 20);

        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 10px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Manuell', badgeX + 30, badgeY + 10);
      }

      // Drag coordinates
      if (isDragging) {
        const coordX = xCenter - 50;
        const coordY = yBase - 50;

        ctx.fillStyle = 'rgba(31, 41, 55, 0.95)';
        ctx.fillRect(coordX, coordY, 100, 30);

        ctx.fillStyle = '#FFFFFF';
        ctx.font = '12px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(
          `X: ${Math.round(currentPosition.x)} Y: ${Math.round(currentPosition.y)}`,
          coordX + 50,
          coordY + 15
        );
      }
    }
  }, [
    captionText,
    currentPosition,
    style,
    fontSize,
    textAreaWidth,
    textAlignment,
    isPositionOverridden,
    isDragging,
    isHovering,
    wrapText,
    getMaxLineWidth,
    calculateTextBounds,
    drawTextWithOutline,
    drawRoundedRect,
    CANVAS_WIDTH,
    CANVAS_HEIGHT,
    LINE_HEIGHT,
    OUTLINE_WIDTH,
    BG_HORIZONTAL_MARGIN,
    BG_VERTICAL_PADDING_TOP,
    BG_VERTICAL_PADDING_BOTTOM,
    BG_BORDER_RADIUS,
    gifMetadata,
    gifImage,
    // Background props (for non-9:16 images)
    hasWrongAspectRatio,
    canvasBackgroundColor,
    backgroundMode,
    backgroundConfig.backgroundColor,
    backgroundConfig.gradientType,
    backgroundConfig.gradientAngle,
    JSON.stringify(backgroundConfig.gradientColors),
    // Font
    fontString
  ]);

  /**
   * Handle mouse down - hit detection
   */
  const handleMouseDown = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas || !captionText) return;

    const rect = canvas.getBoundingClientRect();
    const displayX = e.clientX - rect.left;
    const displayY = e.clientY - rect.top;

    // Convert to canvas coordinates
    const canvasX = displayX / scaleX;
    const canvasY = displayY / scaleY;

    // Check if click is on text
    const ctx = canvas.getContext('2d');
    ctx.font = fontString;

    const lines = wrapText(captionText, textAreaWidth, ctx);
    const bounds = calculateTextBounds(currentPosition, lines, ctx);

    if (isPointInText(canvasX, canvasY, bounds)) {
      // Calculate offset from text center
      const offsetX = canvasX - currentPosition.x;
      const offsetY = canvasY - currentPosition.y;

      setDragStartOffset({ x: offsetX, y: offsetY });
      setIsDragging(true);

      e.preventDefault();
    }
  }, [
    captionText,
    currentPosition,
    fontSize,
    textAreaWidth,
    scaleX,
    scaleY,
    wrapText,
    calculateTextBounds,
    isPointInText,
    fontString
  ]);

  /**
   * Handle mouse move - update position during drag
   */
  const handleMouseMove = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas || !captionText) return;

    const rect = canvas.getBoundingClientRect();
    const displayX = e.clientX - rect.left;
    const displayY = e.clientY - rect.top;

    // Convert to canvas coordinates
    const canvasX = displayX / scaleX;
    const canvasY = displayY / scaleY;

    if (isDragging) {
      // Calculate new position
      let newX = canvasX - dragStartOffset.x;
      let newY = canvasY - dragStartOffset.y;

      // Get text bounds for clamping
      const ctx = canvas.getContext('2d');
      ctx.font = fontString;
      const lines = wrapText(captionText, textAreaWidth, ctx);
      const maxWidth = getMaxLineWidth(lines, ctx);
      const totalHeight = lines.length * LINE_HEIGHT;

      // Clamp within canvas bounds
      newX = Math.max(maxWidth / 2, Math.min(CANVAS_WIDTH - maxWidth / 2, newX));
      newY = Math.max(0, Math.min(CANVAS_HEIGHT - totalHeight, newY));

      setCurrentPosition({ x: Math.round(newX), y: Math.round(newY) });
    } else {
      // Update hover state
      const ctx = canvas.getContext('2d');
      ctx.font = fontString;
      const lines = wrapText(captionText, textAreaWidth, ctx);
      const bounds = calculateTextBounds(currentPosition, lines, ctx);

      setIsHovering(isPointInText(canvasX, canvasY, bounds));
    }
  }, [
    captionText,
    isDragging,
    dragStartOffset,
    currentPosition,
    fontSize,
    textAreaWidth,
    scaleX,
    scaleY,
    wrapText,
    getMaxLineWidth,
    calculateTextBounds,
    isPointInText,
    CANVAS_WIDTH,
    CANVAS_HEIGHT,
    LINE_HEIGHT,
    fontString
  ]);

  /**
   * Handle mouse up - finalize and notify parent
   */
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);

      // Notify parent ONLY on mouseup (avoid re-render loop)
      if (onPositionChange) {
        onPositionChange(currentPosition);
      }
    }
  }, [isDragging, currentPosition, onPositionChange]);

  /**
   * Handle mouse leave - cancel drag
   */
  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    if (isDragging) {
      handleMouseUp();
    }
  }, [isDragging, handleMouseUp]);

  /**
   * Handle reset position
   */
  const handleReset = useCallback(() => {
    if (originalPosition && onResetPosition) {
      setCurrentPosition(originalPosition);
      onResetPosition();
    }
  }, [originalPosition, onResetPosition]);

  /**
   * Handle GIF mouse down - start dragging GIF
   */
  const handleGifMouseDown = useCallback((e) => {
    if (!gifMetadata) return;

    e.preventDefault();
    e.stopPropagation();

    // Convert screen coordinates to canvas coordinates
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    const displayX = e.clientX - rect.left;
    const displayY = e.clientY - rect.top;

    const canvasX = displayX / scaleX;
    const canvasY = displayY / scaleY;

    // Calculate offset from GIF position
    const offsetX = canvasX - gifMetadata.position.x;
    const offsetY = canvasY - gifMetadata.position.y;

    setGifDragStartOffset({ x: offsetX, y: offsetY });
    setIsDraggingGif(true);
  }, [gifMetadata, scaleX, scaleY]);

  /**
   * Handle GIF mouse move - update position during drag
   */
  const handleGifMouseMove = useCallback((e) => {
    if (!isDraggingGif || !gifMetadata) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const displayX = e.clientX - rect.left;
    const displayY = e.clientY - rect.top;

    // Convert to canvas coordinates
    const canvasX = displayX / scaleX;
    const canvasY = displayY / scaleY;

    // Calculate new position
    let newX = canvasX - gifDragStartOffset.x;
    let newY = canvasY - gifDragStartOffset.y;

    // Clamp within canvas bounds
    newX = Math.max(0, Math.min(CANVAS_WIDTH - gifMetadata.size.width, newX));
    newY = Math.max(0, Math.min(CANVAS_HEIGHT - gifMetadata.size.height, newY));

    // Update GIF metadata
    onGifChange({
      ...gifMetadata,
      position: {
        x: Math.round(newX),
        y: Math.round(newY)
      }
    });
  }, [isDraggingGif, gifMetadata, gifDragStartOffset, scaleX, scaleY, onGifChange, CANVAS_WIDTH, CANVAS_HEIGHT]);

  /**
   * Handle GIF mouse up - finalize drag
   */
  const handleGifMouseUp = useCallback(() => {
    if (isDraggingGif) {
      setIsDraggingGif(false);
    }
  }, [isDraggingGif]);

  // Keep redrawRef.current up to date
  useEffect(() => {
    redrawRef.current = redraw;
  }, [redraw]);

  // Load image ONLY when imageUrl changes
  useEffect(() => {
    const img = imageRef.current;

    if (imageUrl) {
      // Reset loading state
      imageLoadedRef.current = false;

      // Clear previous handlers and src
      img.onload = null;
      img.onerror = null;
      img.src = '';

      img.onload = () => {
        imageLoadedRef.current = true;
        if (redrawRef.current) redrawRef.current();
      };

      img.onerror = (error) => {
        console.error('LivePreviewCanvas: Failed to load image:', error);
        imageLoadedRef.current = false;
        if (redrawRef.current) redrawRef.current();
      };

      // Load new image
      img.src = imageUrl;
    } else {
      imageLoadedRef.current = false;
      if (redrawRef.current) redrawRef.current();
    }

    // CLEANUP: Release image resources
    return () => {
      img.onload = null;
      img.onerror = null;
      img.src = '';
      imageLoadedRef.current = false;
    };
  }, [imageUrl]); // Only depend on imageUrl to avoid re-loading image

  // Load GIF when metadata changes
  useEffect(() => {
    if (gifMetadata?.giphy_url) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = gifMetadata.giphy_url;
      img.onload = () => {
        setGifImage(img);
        if (redrawRef.current) redrawRef.current();
      };
      img.onerror = (error) => {
        console.error('LivePreviewCanvas: Failed to load GIF:', error);
        setGifImage(null);
      };
    } else {
      setGifImage(null);
    }
  }, [gifMetadata]);

  // Update container dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerDimensions({
          width: rect.width,
          height: rect.height
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Redraw when specific properties change (not every redraw function change)
  // This prevents flickering during drag by avoiding re-loading the image
  useEffect(() => {
    if (redrawRef.current) {
      redrawRef.current();
    }

    // CLEANUP: Clear canvas on unmount
    return () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      }
    };
  }, [
    captionText,
    currentPosition,
    style.textColor,
    style.backgroundColor,
    style.textHasBackground,
    textAlignment,
    isPositionOverridden,
    isDragging,
    isHovering,
    containerDimensions.width,
    containerDimensions.height,
    fontName
  ]);

  // Sync currentPosition with textPosition prop
  useEffect(() => {
    if (textPosition && !isDragging) {
      setCurrentPosition(textPosition);
    }
  }, [textPosition, isDragging]);

  // Handle GIF drag events globally
  useEffect(() => {
    if (isDraggingGif) {
      window.addEventListener('mousemove', handleGifMouseMove);
      window.addEventListener('mouseup', handleGifMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleGifMouseMove);
        window.removeEventListener('mouseup', handleGifMouseUp);
      };
    }
  }, [isDraggingGif, handleGifMouseMove, handleGifMouseUp]);

  // Redraw canvas when background changes (color or gradient)
  useEffect(() => {
    if (hasWrongAspectRatio && redrawRef.current) {
      redrawRef.current();
    }
  }, [
    hasWrongAspectRatio,
    canvasBackgroundColor,
    backgroundMode,
    backgroundConfig.backgroundColor,
    backgroundConfig.gradientType,
    backgroundConfig.gradientAngle,
    JSON.stringify(backgroundConfig.gradientColors)
  ]);

  return (
    <div className="relative w-full">
      {/* Reset Button */}
      {isPositionOverridden && originalPosition && (
        <div className="absolute top-2 right-2 z-20">
          <button
            onClick={handleReset}
            className="
              bg-white/90 backdrop-blur-sm
              text-gray-700 hover:text-gray-900
              px-3 py-1.5
              rounded-lg
              shadow-md hover:shadow-lg
              transition-all duration-200
              flex items-center gap-1.5
              text-xs font-medium
              border border-gray-200
            "
            title="Position zurücksetzen"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Reset
          </button>
        </div>
      )}

      {/* Canvas Container */}
      <div
        ref={containerRef}
        className="live-preview-container relative"
        style={{
          width: '100%',
          maxWidth: '360px',
          aspectRatio: '1080 / 1920',
          backgroundColor: '#f9fafb',
          borderRadius: '0.75rem',
          overflow: 'hidden',
          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        }}
      >
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            cursor: isDragging ? 'grabbing' : (isHovering ? 'grab' : 'default')
          }}
        />

        {/* GIF Overlay - Browser native animation for better performance */}
        {gifMetadata && gifImage && (
          <img
            src={gifMetadata.giphy_url}
            alt="GIF Sticker"
            crossOrigin="anonymous"
            onMouseDown={handleGifMouseDown}
            style={{
              position: 'absolute',
              left: `${(gifMetadata.position.x / CANVAS_WIDTH) * 100}%`,
              top: `${(gifMetadata.position.y / CANVAS_HEIGHT) * 100}%`,
              width: `${(gifMetadata.size.width / CANVAS_WIDTH) * 100}%`,
              height: `${(gifMetadata.size.height / CANVAS_HEIGHT) * 100}%`,
              opacity: gifMetadata.opacity,
              pointerEvents: 'all',
              cursor: isDraggingGif ? 'grabbing' : 'grab',
              zIndex: 10
            }}
          />
        )}
      </div>

      {/* Help Hint */}
      {!isPositionOverridden && captionText && !isDragging && (
        <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <svg
              className="w-4 h-4 flex-shrink-0"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Ziehe den Text, um die Position anzupassen</span>
          </div>
        </div>
      )}

      {/* Position Info */}
      {isPositionOverridden && (
        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 text-blue-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-blue-700 font-medium">
                Position manuell angepasst
              </span>
            </div>
            <div className="text-xs text-blue-600">
              X: {Math.round(currentPosition.x)} | Y: {Math.round(currentPosition.y)}
            </div>
          </div>
        </div>
      )}

      <div className="sr-only">
        Instagram Story preview showing an image with the caption: {captionText || 'No caption'}
        {isPositionOverridden && ' - Caption position has been manually adjusted'}
      </div>
    </div>
  );
};

LivePreviewCanvas.propTypes = {
  imageUrl: PropTypes.string,
  captionText: PropTypes.string.isRequired,
  style: PropTypes.shape({
    textColor: PropTypes.string.isRequired,
    backgroundColor: PropTypes.string.isRequired,
    textHasBackground: PropTypes.bool.isRequired,
  }).isRequired,
  textPosition: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  originalPosition: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  onPositionChange: PropTypes.func,
  textAreaWidth: PropTypes.number,
  fontSize: PropTypes.number,
  textAlignment: PropTypes.oneOf(['left', 'center', 'right']),
  isPositionOverridden: PropTypes.bool,
  onResetPosition: PropTypes.func,
  gifMetadata: PropTypes.shape({
    giphy_id: PropTypes.string,
    giphy_url: PropTypes.string,
    position: PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
    }),
    size: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }),
    opacity: PropTypes.number,
    layer_order: PropTypes.number,
  }),
  onGifChange: PropTypes.func,
  canvasBackgroundColor: PropTypes.string,
  backgroundMode: PropTypes.oneOf(['solid', 'gradient']),
  backgroundConfig: PropTypes.shape({
    backgroundColor: PropTypes.string,
    gradientType: PropTypes.oneOf(['linear', 'radial']),
    gradientColors: PropTypes.arrayOf(PropTypes.string),
    gradientAngle: PropTypes.number
  }),
  hasWrongAspectRatio: PropTypes.bool,
  backgroundMetadata: PropTypes.shape({
    needs_background: PropTypes.bool,
    background_color: PropTypes.string,
    background_type: PropTypes.oneOf(['solid', 'gradient']),
    gradient_type: PropTypes.oneOf(['linear', 'radial']),
    gradient_colors: PropTypes.arrayOf(PropTypes.string),
    gradient_angle: PropTypes.number,
    canvas_transform: PropTypes.shape({
      scale: PropTypes.number,
      offset_x: PropTypes.number,
      offset_y: PropTypes.number,
      scaled_width: PropTypes.number,
      scaled_height: PropTypes.number
    }),
    original_dimensions: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number
    })
  }),
  fontName: PropTypes.string
};

export default LivePreviewCanvas;
